import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { sharedDynamicsMetadata } from '../consts/metadata-consts';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IDataflowTypeMetadataSource, IDataflowTypeMetadataSink } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { ConnectionAuthKind } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { compact } from 'lodash';
import { GlobalVariables } from 'app/services/globals';

export const onetimeQueryTemplate = '<fetch>\n'
    + '<entity name="<<tableName>>">\n'
    + '<all-attributes />\n'
    + '</entity>\n'
    + '</fetch>\n';

export function getDataflowDynamicsSource(dfType: string, settings: string): IDataflowTypeMetadataSource {
    return {
        type: dfType,
        settings,
        useInlineProjection: true,
        controls: [
            {
                type: 'dataflow-schema-table-selection',
                bind: 'tableName',
                tableNameLabel: stringResources.TableNameDisplay_EntityName,
                tableNamePlaceholder: stringResources.DataFlowLinkedServiceSelectEntityPlaceholder,
                tableRequired: true,
                selectionType: 'table'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox-number',
                    bind: 'timeout',
                    label: stringResources.Timeout,
                    isDataFlowExpression: true
                },
                dynamicContentTextClass: 'input-spaced',
                showErrorIcon: false,
                isDataFlowExpression: true
            },
            {
                type: 'radio-switch',
                bind: 'useQuery',
                label: stringResources.InputLabel,
                options: [
                    {
                        label: stringResources.ModelName_Table,
                        value: false
                    },
                    {
                        label: stringResources.QueryLabel,
                        value: true
                    }
                ],
                cases: [
                    {
                        caseValue: true,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textarea',
                                    bind: 'query',
                                    label: stringResources.QueryLabel,
                                    placeholder: onetimeQueryTemplate,
                                    supportMonaco: true,
                                    monacoLang: 'xml',
                                    isDataFlowExpression: true
                                },
                                dynamicContentTextClass: 'input-spaced',
                                showErrorIcon: false,
                                isDataFlowExpression: true
                            }
                        ]
                    }
                ]
            }
        ]
    };
}

export function getDataflowDynamicsSink(dfType: string, settings: string): IDataflowTypeMetadataSink {
    return {
        type: dfType,
        settings,
        controls: [
            {
                type: 'dataflow-schema-table-selection',
                bind: 'tableName',
                tableNameLabel: stringResources.TableNameDisplay_EntityName,
                tableNamePlaceholder: stringResources.DataFlowLinkedServiceSelectEntityPlaceholder,
                tableRequired: true,
                selectionType: 'table'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox-number',
                    bind: 'timeout',
                    label: stringResources.Timeout,
                    isDataFlowExpression: true
                },
                dynamicContentTextClass: 'input-spaced',
                showErrorIcon: false,
                isDataFlowExpression: true
            },
            {
                type: 'app-dynamic-dataflow-update-method',
                bind: {
                    allowInsert: 'allowInsert',
                    allowDelete: 'allowDelete',
                    allowUpsert: 'allowUpsert',
                    allowUpdate: 'allowUpdate'
                }
            },
            {
                type: 'app-dynamic-dataflow-browse-selection',
                bind: 'alternateKeyName',
                label: stringResources.ModelName_AlternateKeyName,
                optionBind: {
                    label: 'alternateKey',
                    value: 'alternateKey'
                },
                browseEntity: false,
                hide: `{ allowDelete.expression === 'false' && allowUpsert.expression === 'false' && allowUpdate.expression === 'false' }`
            }
        ],
        enableImportSchemaInSink: true,
        enableAlterRow: true
    };
}

export const dynamicsMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.dynamics.linkedServiceType,
        displayName: stringResources.Dynamics365_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-dynamics_365'
        },
        supportDatasetV2: true,
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.ServicePrincipal,
            ConnectionAuthKind.WorkspaceIdentity
        ]),
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'Dynamics365' as ResourceKind
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
        supportFirstClassParameterExperience: true,
        dataflowSource: [getDataflowDynamicsSource(DatasetFormatType.Dynamics, adfModelTypeMap.dynamics.dataflowSettings)],
        dataflowSink: [getDataflowDynamicsSink(DatasetFormatType.Dynamics, adfModelTypeMap.dynamics.dataflowSettings)],
        isFirstClass: true,
        tableNameAlias: 'entityName',
        onetimeQueryTemplate,
        timedQueryTemplate: '<fetch>\n'
            + '<entity name="<<tableName>>">\n'
            + '<all-attributes />\n'
            + '<filter type="and">\n'
            + '<condition attribute ="<<columnName>>" operator="between">\n'
            + '<value>@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-dd HH:mm\')}</value>\n'
            + '<value>@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-dd HH:mm\')}</value>\n'
            + '</condition>\n'
            + '</filter>\n'
            + '</entity>\n'
            + '</fetch>\n',
        deltaLoadQueryTemplate: '<fetch>\n'
            + '<entity name="<<tableName>>">\n'
            + '<all-attributes />\n'
            + '<filter type="and">\n'
            + '<condition attribute ="<<columnName>>" operator="between">\n'
            + '<value><<startValue>></value>\n'
            + '<value><<endValue>></value>\n'
            + '</condition>\n'
            + '</filter>\n'
            + '</entity>\n'
            + '</fetch>\n',
        fetchTableNameFromItemPath: true,
        copySinkTypes: [adfModelTypeMap.dynamics.copySinkType],
        copySourceTypes: [adfModelTypeMap.dynamics.copySourceType],
        category: ['ServicesApps'],
        controls: sharedDynamicsMetadata
    };

    return metadata;
};
